import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FilterX, Plus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { aceitoDaUrl, uuidDaUrl, limparFiltrosEFocarBusca } from '../../componentes/filtros-lista';
import { filtrosNaUrl } from '../../componentes/filtros-visao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { data, dinheiro, nomeParceiro } from '../../nucleo/formato';
import { AvisoModo, useSessaoFluxo } from './AvisoModo';
import { DetalhePedido } from './DetalhePedido';
import { FormPedido } from './FormPedido';
import { COR_PEDIDO, ESTADOS_PEDIDO, type Pedido } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

/** `a_receber` e `atrasados` da URL: o web só liga com `true` (o que a API aceita e o próprio filtro grava). */
const SIM = ['true'] as const;

/** Aba Pedidos (M10.1): do rascunho ao recebimento. Filtros ficam na URL, então links de outras telas chegam já filtrados. */
export function Pedidos() {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  // Fase 2 (W8): no modo simplificado (ou sem Estoque, no recebimento) as ações do fluxo detalhado somem; a leitura fica.
  const s = useSessaoFluxo();
  const [busca, setBusca] = useSearchParams();
  const [novo, setNovo] = useState(false);
  // Review r29 robustez (O2): `?pedido=` que não é uuid é ignorado (antes a ficha abria com 422 e "Não foi possível carregar").
  const [aberto, setAberto] = useState<string | null>(uuidDaUrl(busca.get('pedido')) || null);
  // Link de outra tela (`?pedido=<id>`) abre a ficha mesmo com a aba já aberta.
  useEffect(() => {
    const id = uuidDaUrl(busca.get('pedido'));
    if (id) setAberto(id);
  }, [busca]);
  const filtro = (k: string) => busca.get(k) ?? '';
  const mudar = (k: string, v: string) =>
    setBusca(
      (atual) => {
        const n = new URLSearchParams(atual);
        if (v) n.set(k, v);
        else n.delete(k);
        return n;
      },
      { replace: true },
    );
  // Review r29 robustez (B2): o mesmo saneamento das outras listas (r26/r27/r28). Valor que a API recusaria (`?estado=ABERTOS`,
  // `?atrasados=sim`, fornecedor ou produto que não é uuid, nulo ou max) é ignorado; antes ia cru e a lista caía em "Indisponível".
  const params = {
    estado: aceitoDaUrl(filtro('estado'), ESTADOS_PEDIDO) || undefined,
    a_receber: aceitoDaUrl(filtro('a_receber'), SIM) || undefined,
    atrasados: aceitoDaUrl(filtro('atrasados'), SIM) || undefined,
    fornecedor_id: uuidDaUrl(filtro('fornecedor_id')) || undefined,
    produto_id: uuidDaUrl(filtro('produto_id')) || undefined,
  };
  const filtrando = !!(params.estado || params.a_receber || params.atrasados || params.fornecedor_id || params.produto_id);

  const colunas: Coluna<Pedido>[] = [
    { chave: 'numero', rotulo: t('campos.numero'), mono: true, ordenavel: true, render: (p) => <span className="font-medium">{p.numero}</span> },
    {
      chave: 'fornecedor_nome',
      rotulo: t('campos.fornecedor'),
      principal: true,
      render: (p) => (
        <span className="flex min-w-0 max-w-sm flex-col">
          <span className="truncate font-medium" title={nomeParceiro(p.fornecedor_nome)}>{nomeParceiro(p.fornecedor_nome)}</span>
          <TextoCortado className="text-xs text-tinta-3">{t('campos.itensQtd', { count: p.itens })}</TextoCortado>
        </span>
      ),
    },
    {
      chave: 'prazo_entrega',
      rotulo: t('campos.prazoEntrega'),
      ordenavel: true,
      mono: true,
      render: (p) =>
        p.atrasado ? (
          <span className="flex flex-col font-medium text-laranja">
            <span>{data(p.prazo_entrega)}</span>
            <span className="text-xs">{t('pedido.atrasadoDias', { count: p.dias_atraso })}</span>
          </span>
        ) : p.prazo_entrega ? (
          data(p.prazo_entrega)
        ) : (
          ''
        ),
    },
    { chave: 'condicao', rotulo: t('campos.condicao'), oculta: true, mono: true, render: (p) => (p.condicao_pagamento === 'avista' ? t('pedido.avista') : p.condicao_pagamento) },
    { chave: 'total', rotulo: t('campos.total'), ordenavel: true, alinhar: 'direita', mono: true, render: (p) => dinheiro(p.total_centavos, p.moeda) },
    {
      chave: 'recebido',
      rotulo: t('campos.recebido'),
      alinhar: 'direita',
      mono: true,
      render: (p) => (p.estado === 'rascunho' || p.estado === 'enviado' || p.estado === 'cancelado' ? '' : dinheiro(p.recebido_centavos, p.moeda)),
    },
    { chave: 'created_at', rotulo: t('campos.criadoEm'), ordenavel: true, oculta: true, mono: true, render: (p) => data(p.created_at) },
    {
      chave: 'estado',
      rotulo: t('campos.estado'),
      render: (p) => (
        <span className="flex flex-wrap gap-1">
          <Selo cor={COR_PEDIDO[p.estado]}>{t(`estadoPedido.${p.estado}`)}</Selo>
          {p.atrasado && <Selo cor="laranja">{t('estadoPedido.atrasado')}</Selo>}
        </span>
      ),
    },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <AvisoModo />
      <AcaoPrimaria>
        {s.pode('compras.excluir') && <BotaoLixeira entidades={['compras.pedido']} />}
        {s.pode('compras.criar') && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t('acoes.novoPedido')}
          </Botao>
        )}
      </AcaoPrimaria>

      <section aria-label={tn('comum.filtrar')} className="flex flex-wrap items-end gap-2">
        <div className="w-full sm:w-56">
          <Selecao aria-label={t('campos.estado')} vazio={t('filtros.todosEstados')} opcoes={ESTADOS_PEDIDO.map((e) => ({ valor: e, rotulo: t(`estadoPedido.${e}`) }))} value={params.estado ?? ''} onChange={(e) => mudar('estado', e.target.value)} />
        </div>
        <div className="w-full sm:w-56">
          <Selecao aria-label={t('filtros.situacao')} vazio={t('filtros.qualquerSituacao')} opcoes={[{ valor: 'a_receber', rotulo: t('filtros.aReceber') }, { valor: 'atrasados', rotulo: t('filtros.soAtrasados') }]} value={params.a_receber ? 'a_receber' : params.atrasados ? 'atrasados' : ''} onChange={(e) => setBusca((atual) => { const n = new URLSearchParams(atual); n.delete('a_receber'); n.delete('atrasados'); if (e.target.value === 'a_receber') n.set('a_receber', 'true'); if (e.target.value === 'atrasados') n.set('atrasados', 'true'); return n; }, { replace: true })} />
        </div>
        <Botao className="whitespace-nowrap" variante="fantasma" icone={<FilterX className="size-4" />} disabled={!filtrando} onClick={() => limparFiltrosEFocarBusca(() => setBusca({}, { replace: true }))} data-limpar-filtros="">
          {t('filtros.limpar')}
        </Botao>
      </section>

      <Tabela<Pedido>
        tela="compras.pedidos"
        caminho="/compras/pedidos"
        params={params}
        filtros={{ estado: filtro('estado'), a_receber: filtro('a_receber'), atrasados: filtro('atrasados'), fornecedor_id: filtro('fornecedor_id'), produto_id: filtro('produto_id') }}
        aoAplicarFiltros={(f) => setBusca((atual) => filtrosNaUrl(atual, f), { replace: true })}
        colunas={colunas}
        entidade="compras.pedido"
        aoClicar={(p) => setAberto(p.id)}
        vazio={<Vazio texto={filtrando ? t('vazio.filtro') : t('vazio.pedidos')} />}
      />

      <FormPedido aberto={novo} aoFechar={() => setNovo(false)} aoSalvar={(id) => setAberto(id)} />
      <DetalhePedido
        id={aberto}
        aoFechar={() => {
          setAberto(null);
          if (busca.get('pedido')) mudar('pedido', '');
        }}
      />
    </div>
  );
}
